import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, Maximize2 } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import type { ManualTarget } from '@/shared/config/manual';

import { HelpLink } from '../../help-link';
import { HorizontalScroll, useScrollEdges } from '../model/use-horizontal-scroll';
import { TableScrollControls } from './table-scroll-controls';

interface TableToolbarProps {
  title?: string;
  total?: number;
  helpTarget?: ManualTarget | null;
  enableColumnCustomization?: boolean;
  enableExport?: boolean;
  enableFullscreen?: boolean;
  onCustomizeColumns?: () => void;
  onExport?: () => void;
  isExporting?: boolean;
  onFullscreen?: () => void;
  scroll?: HorizontalScroll;
}

export function TableToolbar({
  title,
  total,
  helpTarget,
  enableColumnCustomization = true,
  enableExport = false,
  enableFullscreen = true,
  onCustomizeColumns,
  onExport,
  isExporting = false,
  onFullscreen,
  scroll
}: TableToolbarProps) {
  const { t } = useTranslation();
  const { canScrollStart, canScrollEnd } = useScrollEdges(scroll);
  const hasViewActions = enableColumnCustomization || enableExport || enableFullscreen;
  const optionsLabel = t('common.tableOptions', 'Table options');
  const fullscreenLabel = t('welcome.map.fullscreen.label', 'Full screen');

  return (
    <div className="flex justify-between items-center p-6 pb-4">
      {title && (
        <div className="flex items-center gap-1">
          <h3 className="text-foreground text-xl font-semibold">{title}</h3>
          <HelpLink target={helpTarget} className="h-7 w-7" />
          {total != null && (
            <span className="ml-2 text-sm text-muted-foreground">
              {total} {t('gen.numres.label', 'results found')}
            </span>
          )}
        </div>
      )}
      {/* ml-auto keeps the actions at the right edge when there is no title —
          justify-between alone would pull a lone child to the left */}
      <div className="flex items-center gap-2 ml-auto">
        {scroll && (canScrollStart || canScrollEnd) && (
          <>
            <TableScrollControls scroll={scroll} />
            {hasViewActions && <Separator orientation="vertical" className="data-[orientation=vertical]:h-5" />}
          </>
        )}

        {(enableColumnCustomization || enableExport) && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <ActionButton
                    intent="view"
                    size="icon"
                    className="h-9 w-9"
                    aria-label={optionsLabel}
                    data-testid="table-options-button">
                    <SlidersHorizontal className="w-4 h-4" />
                  </ActionButton>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{optionsLabel}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-48">
              {enableColumnCustomization && (
                <DropdownMenuItem onClick={onCustomizeColumns}>
                  {t('gen.customize.cols.label', 'Customize Columns')}
                </DropdownMenuItem>
              )}
              {enableExport && (
                <DropdownMenuItem onClick={onExport} disabled={isExporting}>
                  {t('common.exportTable', 'Export Table')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {enableFullscreen && (
          <Tooltip>
            <TooltipTrigger asChild>
              <ActionButton
                intent="view"
                size="icon"
                className="h-9 w-9"
                aria-label={fullscreenLabel}
                onClick={onFullscreen}>
                <Maximize2 className="w-4 h-4" />
              </ActionButton>
            </TooltipTrigger>
            <TooltipContent>{fullscreenLabel}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
